import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
    Moon,
    Sun,
    LogOut,
    User,
    Lock,
    ChevronRight,
    Settings,
    CreditCard,
    MonitorSmartphone,
    Download
} from 'lucide-react';
import { supabase } from '@/config/supabase';
import { useToast } from '@/contexts/ToastContext';
import { usePWAInstall } from '@/hooks/usePWAInstall';

export const SettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const { signOut, user, profile, setActiveRole } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const { success, error: toastError, info } = useToast();
    const [resettingPassword, setResettingPassword] = useState(false);
    const { isInstallable, installPWA } = usePWAInstall();

    // Load saved startup screen preference, default to 'dashboard'
    const [startupScreen, setStartupScreen] = useState(() => {
        return localStorage.getItem('majengo_startup_screen') || 'dashboard';
    });

    const handleStartupScreenChange = (screen: string) => {
        setStartupScreen(screen);
        localStorage.setItem('majengo_startup_screen', screen);
        success('Preference saved', `App will now start on ${screen === 'pos' ? 'Live POS' : 'Dashboard'}`);
    };

    const handlePasswordReset = async () => {
        if (!user?.email) {
            info('No email found', 'No email is associated with your account.');
            return;
        }

        if (window.confirm(`A password reset link will be sent to ${user.email}. Do you want to continue?`)) {
            setResettingPassword(true);
            try {
                const { error } = await supabase.auth.resetPasswordForEmail(user.email);
                if (error) throw error;
                success('Reset link sent', 'Password reset link sent to your email.');
            } catch (error: any) {
                toastError('Failed to send link', error.message || 'Failed to send reset link.');
            } finally {
                setResettingPassword(false);
            }
        }
    };

    const handleRoleChange = async (role: any) => {
        try {
            if (profile?.active_role !== role) {
                await setActiveRole(role);
            }
            const r = role.toLowerCase();
            if (r.includes('business')) navigate('/business');
            else if (r.includes('landlord')) navigate('/landlord');
            else if (r.includes('staff')) navigate('/staff');
            else if (r.includes('tenant') || r.includes('resident')) navigate('/tenant');
            else if (r.includes('admin')) navigate('/admin');
            else navigate('/');
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-32 md:pb-6">
            <header className="sticky top-0 z-40 bg-light-bg/95 dark:bg-dark-bg/95 backdrop-blur-sm shadow-sm border-b border-light-border dark:border-dark-border">
                <div className="w-full px-4 md:px-6 py-4 flex items-center justify-between">
                    <h1 className="text-xl md:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                        <Settings size={24} className="text-primary dark:text-golden" />
                        Settings & Privacy
                    </h1>
                </div>
            </header>

            <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-8 mt-4">

                {/* Appearance */}
                <section>
                    <h2 className="text-sm font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-3">Appearance</h2>
                    <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3">
                                {isDarkMode ? (
                                    <Moon className="text-primary dark:text-golden" size={20} />
                                ) : (
                                    <Sun className="text-primary dark:text-golden" size={20} />
                                )}
                                <span className="font-medium text-light-text dark:text-dark-text">Dark Mode</span>
                            </div>

                            <button
                                onClick={toggleTheme}
                                className={`w-12 h-6 rounded-full transition-colors relative ${isDarkMode ? 'bg-primary dark:bg-golden' : 'bg-gray-300 dark:bg-gray-600'}`}
                            >
                                <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                        </div>
                    </div>
                </section>

                {/* Roles */}
                {profile?.roles && Array.isArray(profile.roles) && profile.roles.length > 1 && (
                    <section>
                        <h2 className="text-sm font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-2">Switch Role</h2>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle mb-4">Select your active role to change your default dashboard.</p>
                        <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden divide-y divide-light-border dark:divide-dark-border">
                            {profile.roles.map((role) => {
                                const isActive = profile.active_role === role;
                                return (
                                    <button
                                        key={role}
                                        onClick={() => handleRoleChange(role)}
                                        className="w-full flex items-center justify-between p-4 hover:bg-light-border dark:hover:bg-dark-border transition-colors text-left"
                                    >
                                        <span className={`font-medium ${isActive ? 'text-primary dark:text-golden' : 'text-light-text dark:text-dark-text'}`}>
                                            {role}
                                        </span>
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-primary dark:border-golden' : 'border-light-subtle dark:border-dark-subtle'}`}>
                                            {isActive && <div className="w-2.5 h-2.5 rounded-full bg-primary dark:bg-golden" />}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </section>
                )}

                {/* App Preferences */}
                <section>
                    <h2 className="text-sm font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-3">App Preferences</h2>
                    <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden divide-y divide-light-border dark:divide-dark-border">
                        
                        {/* Startup Screen */}
                        <div className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <MonitorSmartphone className="text-light-subtle dark:text-dark-subtle" size={20} />
                                <span className="font-medium text-light-text dark:text-dark-text">Default Startup Screen</span>
                            </div>
                            <p className="text-xs text-light-subtle dark:text-dark-subtle mb-3">Choose which screen opens first when you select a business.</p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => handleStartupScreenChange('dashboard')}
                                    className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                                        startupScreen === 'dashboard' 
                                            ? 'bg-primary/10 border-primary text-primary dark:bg-golden/10 dark:border-golden dark:text-golden' 
                                            : 'bg-light-bg border-light-border text-light-subtle hover:bg-gray-50 dark:bg-dark-bg dark:border-dark-border dark:text-dark-subtle dark:hover:bg-gray-800'
                                    }`}
                                >
                                    Dashboard
                                </button>
                                <button
                                    onClick={() => handleStartupScreenChange('pos')}
                                    className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                                        startupScreen === 'pos' 
                                            ? 'bg-primary/10 border-primary text-primary dark:bg-golden/10 dark:border-golden dark:text-golden' 
                                            : 'bg-light-bg border-light-border text-light-subtle hover:bg-gray-50 dark:bg-dark-bg dark:border-dark-border dark:text-dark-subtle dark:hover:bg-gray-800'
                                    }`}
                                >
                                    Live POS
                                </button>
                            </div>
                        </div>

                        {/* PWA Install */}
                        <button
                            onClick={() => {
                                if (isInstallable) {
                                    installPWA();
                                } else {
                                    info(
                                        'Manual Installation Required', 
                                        "To install, look for the install icon (monitor with a down arrow) on the right side of your browser's address bar, or check your browser menu (Chrome/Edge)."
                                    );
                                }
                            }}
                            className="w-full flex items-center justify-between p-4 hover:bg-light-border dark:hover:bg-dark-border transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <Download className="text-primary dark:text-golden" size={20} />
                                <div className="flex flex-col text-left">
                                    <span className="font-medium text-light-text dark:text-dark-text">Install Desktop App</span>
                                    {!isInstallable && <span className="text-[10px] text-light-subtle dark:text-dark-subtle">Via browser address bar</span>}
                                </div>
                            </div>
                            <ChevronRight className="text-light-subtle dark:text-dark-subtle" size={20} />
                        </button>
                    </div>
                </section>

                {/* Account */}
                <section>
                    <h2 className="text-sm font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-3">Account</h2>
                    <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border overflow-hidden divide-y divide-light-border dark:divide-dark-border">
                        <button
                            onClick={() => navigate('/profile')}
                            className="w-full flex items-center justify-between p-4 hover:bg-light-border dark:hover:bg-dark-border transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <User className="text-light-subtle dark:text-dark-subtle" size={20} />
                                <span className="font-medium text-light-text dark:text-dark-text">Account Information</span>
                            </div>
                            <ChevronRight className="text-light-subtle dark:text-dark-subtle" size={20} />
                        </button>

                        <button
                            onClick={() => navigate('/pricing')}
                            className="w-full flex items-center justify-between p-4 hover:bg-light-border dark:hover:bg-dark-border transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <CreditCard className="text-light-subtle dark:text-dark-subtle" size={20} />
                                <span className="font-medium text-light-text dark:text-dark-text">Subscriptions & Pricing</span>
                            </div>
                            <ChevronRight className="text-light-subtle dark:text-dark-subtle" size={20} />
                        </button>

                        <button
                            onClick={handlePasswordReset}
                            disabled={resettingPassword}
                            className="w-full flex items-center justify-between p-4 hover:bg-light-border dark:hover:bg-dark-border transition-colors disabled:opacity-50"
                        >
                            <div className="flex items-center gap-3">
                                <Lock className="text-light-subtle dark:text-dark-subtle" size={20} />
                                <span className="font-medium text-light-text dark:text-dark-text">
                                    {resettingPassword ? 'Sending Link...' : 'Change Password'}
                                </span>
                            </div>
                            <ChevronRight className="text-light-subtle dark:text-dark-subtle" size={20} />
                        </button>
                    </div>
                </section>

                {/* Logout */}
                <section className="pt-4">
                    <button
                        onClick={signOut}
                        className="w-full flex items-center justify-center gap-2 p-4 bg-light-card dark:bg-dark-card rounded-xl border border-danger dark:border-danger/50 text-danger dark:text-danger-dark font-medium hover:bg-danger/10 transition-colors"
                    >
                        <LogOut size={20} />
                        Logout
                    </button>
                </section>

            </div>
        </div>
    );
};

export default SettingsPage;
